import PropTypes from 'prop-types';
import {
    useFloating,
    useInteractions,
    useRole,
    useDismiss,
    FloatingPortal,
    FloatingOverlay,
    FloatingFocusManager,
} from '@floating-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from 'Utils/cn';
import useFloatingProps from 'Hooks/useFloatingProps';
import OutlineClose from 'Components/Atoms/OutlineIcons/OutlineClose';
import IconButton from 'Components/Atoms/IconButton/IconButton';

const Overlay = motion(FloatingOverlay);

export default function DialogFullscreen(props) {
    const { open, onClose, children, className } = useFloatingProps(props);

    const { refs, context } = useFloating({
        open,
        onOpenChange: (open) => {
            if (!open) onClose?.();
        },
    });

    const { getFloatingProps } = useInteractions([useRole(context, { role: 'dialog' }), useDismiss(context)]);

    return (
        <FloatingPortal>
            <AnimatePresence>
                {open && (
                    <Overlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.15 } }}
                        className="fixed inset-0 bg-white z-modal-backdrop flex items-center justify-center"
                        lockScroll
                    >
                        <FloatingFocusManager context={context}>
                            <motion.div
                                initial={{ opacity: 0, y: -28 }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                    transition: { delay: 0.2, duration: 0.15 },
                                }}
                                exit={{ opacity: 0, y: -28 }}
                                {...getFloatingProps({
                                    ref: refs.setFloating,
                                    className: cn('bg-white w-full h-full flex flex-col', className),
                                })}
                            >
                                <div className="flex justify-end md:absolute md:right-2 md:top-2 z-20">
                                    {onClose && (
                                        <IconButton variant="ghost" icon={<OutlineClose />} onClick={onClose} />
                                    )}
                                </div>
                                <div className="overflow-auto scrollbar-thin px-3 grow">{children}</div>
                            </motion.div>
                        </FloatingFocusManager>
                    </Overlay>
                )}
            </AnimatePresence>
        </FloatingPortal>
    );
}

DialogFullscreen.propTypes = {
    open: PropTypes.bool,
    children: PropTypes.node.isRequired,
    onClose: PropTypes.func,
    className: PropTypes.string,
};
